import { useMemo } from "react";
import { ChevronDown, ChevronUp, Users } from "lucide-react";
import { chatRowClasses } from "./chatUtils";
import ChatChannelList from "./ChatChannelList";
import ChatGroupList from "./ChatGroupList";
import ChatDmList from "./ChatDmList";
import ChatArchivedList from "./ChatArchivedList";

const ChatTeamDirectory = ({
  teamConversations,
  archivedIds,
  currentTeamId,
  unreadCounts,
  mutedConversations,
  onlineUsers,
  isExpanded,
  expandedTeamSubs,
  onToggleDir,
  onToggleSub,
  onSelect,
}) => {
  const { channels, groups, dms } = teamConversations;

  const visibleChannels = useMemo(
    () => channels.filter((c) => !archivedIds.includes(c.team_id)),
    [channels, archivedIds],
  );
  const visibleGroups = useMemo(
    () => groups.filter((g) => !archivedIds.includes(g.team_id)),
    [groups, archivedIds],
  );
  const visibleDms = useMemo(
    () => dms.filter((d) => !archivedIds.includes(d.team_id)),
    [dms, archivedIds],
  );
  const archivedConvs = useMemo(
    () =>
      [...channels, ...groups, ...dms].filter((c) =>
        archivedIds.includes(c.team_id),
      ),
    [channels, groups, dms, archivedIds],
  );

  const allTeamItems = useMemo(
    () => [...channels, ...groups, ...dms],
    [channels, groups, dms],
  );

  // Active conversation for collapsed-state peek
  const activeTeamConv = currentTeamId
    ? allTeamItems.find((c) => c.team_id === currentTeamId)
    : null;
  const activeLabel = activeTeamConv
    ? activeTeamConv.name || activeTeamConv.dm_display_name || "Conversation"
    : null;

  return (
    <div>
      <button
        onClick={() => onToggleDir("team")}
        className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
      >
        <Users
          className="h-3 w-3 shrink-0"
          strokeWidth={2}
          style={{ color: "#22C55E" }}
        />
        <span className="flex-1 text-left font-medium">Team</span>
        {!isExpanded &&
          (() => {
            const total = allTeamItems.reduce(
              (sum, c) => sum + (unreadCounts[c.team_id] || 0),
              0,
            );
            return total > 0 ? (
              <span className="bg-sky-500 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shrink-0">
                {total > 99 ? "99+" : total}
              </span>
            ) : null;
          })()}
        {isExpanded ? (
          <ChevronUp className="h-3 w-3 shrink-0 text-zinc-400" strokeWidth={2} />
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0 text-zinc-400" strokeWidth={2} />
        )}
      </button>

      {/* Collapsed: active conversation peek */}
      {!isExpanded && activeTeamConv && (
        <div className="ml-2 border-l border-zinc-200/60 dark:border-zinc-700/40 pl-2">
          <button
            onClick={() => onSelect(activeTeamConv.team_id)}
            title={activeLabel}
            className={chatRowClasses(true)}
          >
            <span className="truncate">{activeLabel}</span>
          </button>
        </div>
      )}

      {/* Expanded: sub-sections */}
      {isExpanded && (
        <div className="ml-2 border-l border-emerald-200/60 dark:border-emerald-800/30 pl-2">
          <ChatChannelList
            channels={visibleChannels}
            currentTeamId={currentTeamId}
            unreadCounts={unreadCounts}
            mutedConversations={mutedConversations}
            onSelect={onSelect}
          />
          <ChatGroupList
            groups={visibleGroups}
            expanded={expandedTeamSubs.groups}
            currentTeamId={currentTeamId}
            unreadCounts={unreadCounts}
            mutedConversations={mutedConversations}
            onToggle={onToggleSub}
            onSelect={onSelect}
          />
          <ChatDmList
            dms={visibleDms}
            expanded={expandedTeamSubs.dms}
            currentTeamId={currentTeamId}
            unreadCounts={unreadCounts}
            mutedConversations={mutedConversations}
            onlineUsers={onlineUsers}
            onToggle={onToggleSub}
            onSelect={onSelect}
          />
          <ChatArchivedList
            conversations={archivedConvs}
            expanded={expandedTeamSubs.archived}
            currentTeamId={currentTeamId}
            onToggle={onToggleSub}
            onSelect={onSelect}
          />

          {/* Empty state */}
          {visibleChannels.length === 0 &&
            visibleGroups.length === 0 &&
            visibleDms.length === 0 &&
            archivedConvs.length === 0 && (
              <p className="px-2 py-1 text-[11px] text-zinc-400 dark:text-zinc-600 italic">
                No conversations yet
              </p>
            )}
        </div>
      )}
    </div>
  );
};

export default ChatTeamDirectory;
